import {
  useRef,
  useState,
  useEffect,
} from 'react';
import type {
  ChangeEvent,
  KeyboardEvent,
} from 'react';
import classNames from 'classnames';
import {
  useItems,
  useDomains,
} from '@dinosave/core/stores';
import type {
  ItemEntity,
} from '@dinosave/core/types';
import {
  imageToBase64,
} from '@dinosave/core/utils';
import {
  validateUrl,
} from '@dinosave/core/validations';
import {
  scrape,
} from '@dinosave/scraping';
import posthog from '@/analytics/posthog';
import type {
  Timeout,
} from '@/types';
import Button from '@/ui/components/Button';
import Input from '@/ui/components/Input';
import Text from '@/ui/components/Text';
import {
  LinkIcon,
  DownloadIcon,
} from '@/ui/icons';

import styles from './ItemUrl.module.css';

const HINT_TIMEOUT = 3000;
const CANCEL_TIMEOUT = 20000;

type ItemUrlProps = {
  invalid?: boolean;
  tabIndex?: number;
  defaultValue?: string;
  onChange?: (url: string) => void;
  onSuccess?: (item: ItemEntity) => void;
}

const ItemUrl = (props: ItemUrlProps) => {
  // ##########################################################################
  // PROPS
  // ##########################################################################

  const {
    invalid = false,
    tabIndex,
    defaultValue = '',
    onChange,
    onSuccess,
  } = props;

  // ##########################################################################
  // STORES
  // ##########################################################################

  const findDomain = useDomains(s => s.findDomain);

  const getItemHTML = useItems(s => s.getItemHTML);

  // ##########################################################################
  // STATE
  // ##########################################################################

  const urlRef = useRef<HTMLInputElement>(null);
  const hintTimeoutRef = useRef<Timeout>(null);
  const cancelTimeoutRef = useRef<Timeout>(null);

  const [url, setURL] = useState(defaultValue);
  const [urlIsInvalid, setURLIsInvalid] = useState(invalid);
  const [loading, setLoading] = useState(false);
  const [nameFailed, setNameFailed] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const [priceFailed, setPriceFailed] = useState(false);
  const [hintOpen, setHintOpen] = useState(false);

  // ##########################################################################
  // CONSTANTS
  // ##########################################################################

  const failed = nameFailed &&
                 imageFailed &&
                 priceFailed;

  // ##########################################################################
  // FUNCTIONS
  // ##########################################################################

  const handleURLChange = (e: ChangeEvent<HTMLInputElement>) => {
    const url = e.target.value.trim();
    setURL(url);
    setURLIsInvalid(false);

    if (
      onChange
    ) onChange(url);
  };

  const handleKeydown = async (e?: KeyboardEvent<HTMLInputElement>) => {
    const isSubmitted = e?.key === 'Enter';
    if (
      !isSubmitted
    ) return;

    e?.stopPropagation();

    handleSubmit();
  };

  const handleSubmit = () => {
    if (
      loading
    ) return;

    parseItem(url);
  };

  const parseItem = async (url: string) => {
    try {
      const isURLValid = validateUrl(url);
      setURLIsInvalid(!isURLValid);

      if (
        !isURLValid
      ) return;

      posthog.capture(posthog.events.ITEM_URL_PARSING, { url });

      setLoading(true);
      setHintOpen(false);

      hintTimeoutRef.current = setTimeout(
        () => {
          setFailed(false);
          setHintOpen(true);
        },
        HINT_TIMEOUT,
      );

      cancelTimeoutRef.current = setTimeout(
        () => {
          setFailed(true);
          setLoading(false);
        },
        CANCEL_TIMEOUT,
      );

      const uri = new URL(url);
      const domain = await findDomain(uri.hostname);

      const itemHTML = await getItemHTML(url);
      posthog.capture(posthog.events.ITEM_URL_HTML_RECEIVED, { url });

      const parser = new DOMParser();
      const doc = parser.parseFromString(itemHTML, 'text/html');

      posthog.capture(posthog.events.ITEM_URL_HTML_PARSED, { url });

      const data = scrape(uri.host, doc, domain);

      posthog.capture(posthog.events.ITEM_URL_DATA_SCRAPED, {
        url,
        data,
      });

      const image = await imageToBase64(data.imageUrl);

      posthog.capture(posthog.events.ITEM_URL_IMAGE_PARSED, { url });

      const failed = !image &&
                     !data.name &&
                     !data.currency &&
                     !data.currentPrice;

      if (
        !image
      ) setImageFailed(true);

      if (
        !data.name
      ) setNameFailed(true);

      if (
        !data.currency ||
        !data.currentPrice
      ) setPriceFailed(true);

      const item = {
        ...data,
        image,
        url,
      } as ItemEntity;

      if (
        onSuccess
      ) onSuccess(item);

      setFailed(failed);
    } catch {
      posthog.capture(posthog.events.ITEM_URL_PARSING_FAILED, { url });
      setFailed(true);
    } finally {
      setLoading(false);
      clearTimeout(hintTimeoutRef.current);
      clearTimeout(cancelTimeoutRef.current);
    }
  };

  const setFailed = (failed: boolean) => {
    setHintOpen(failed);
    setNameFailed(failed);
    setImageFailed(failed);
    setPriceFailed(failed);
    clearTimeout(hintTimeoutRef.current);
    clearTimeout(cancelTimeoutRef.current);
  };

  // ##########################################################################
  // EFFECTS
  // ##########################################################################

  useEffect(() => {
    if (
      invalid
    ) setURLIsInvalid(invalid);
  }, [invalid]);

  // ##########################################################################
  // COMPUTED STYLES
  // ##########################################################################

  const itemUrlClassName = classNames({
    [styles.itemUrl]: true,
    [styles.hint]: hintOpen,
    [styles.slow]: hintOpen && loading,
  });

  // ##########################################################################
  // RENDER
  // ##########################################################################

  return (
    <div className={itemUrlClassName}>
      <div className={styles.form}>
        <Input
          ref={urlRef}
          value={url}
          onChange={handleURLChange}
          onKeyDown={handleKeydown}
          placeholder="https://www.amazon.com/crocs-unisex-classic..."
          roundness="rounded"
          readOnly={loading}
          invalid={urlIsInvalid}
          autoComplete="off"
          spellCheck="false"
          icon={LinkIcon}
          tabIndex={tabIndex}
          className={styles.input}
        />

        <div className={styles.button}>
          <Button
            kind="outline"
            color="primary"
            type="submit"
            roundness="round"
            loading={loading}
            disabled={loading}
            onClick={handleSubmit}
            centered
          >
            <DownloadIcon color="var(--primary-600)" />
          </Button>
        </div>
      </div>

      <div className={styles.hint}>
        <div className={styles.hintText}>
          <Text
            kind="caption"
            size="large"
            color="neutral-500"
          >
            {loading && !failed ? (
              'That’s a tough one... 😓'
            ) : (
              'Failed to retrieve this item. 😐'
            )}
          </Text>
        </div>

        <div className={styles.hintText}>
          <Text
            kind="caption"
            size="large"
            color="neutral-500"
          >
            {loading && !failed ? (
              'If it’s taking too long, save it manually below.'
            ) : (
              'You still can save it manually below.'
            )}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default ItemUrl;
